import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight }  from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';

interface AttendanceRecord {
  attendance_date: string;
  status: 'present' | 'absent' | 'late' | 'leave';
}

interface AttendanceCalendarProps {
  records: AttendanceRecord[];
}

const statusColors: Record<string, string> = {
  present: 'bg-lime-500',
  absent: 'bg-rose-500',
  late: 'bg-tangerine-500',
  leave: 'bg-electric-500',
};

const statusLabels: Record<string, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  leave: 'Leave',
};

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AttendanceCalendar({ records }: AttendanceCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const recordsByDate = useMemo(() => {
    const map: Record<string, string> = {};
    records.forEach((record) => {
      const dateKey = record.attendance_date;
      map[dateKey] = record.status;
    });
    return map;
  }, [records]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const handlePrevMonth = () => setCurrentMonth((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));

  return (
    <div className="glass-card rounded-2xl p-3 sm:p-5 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <button
          onClick={handlePrevMonth}
          className="p-1.5 sm:p-2 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text-secondary dark:text-dark-text-secondary transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          onClick={handleNextMonth}
          className="p-1.5 sm:p-2 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text-secondary dark:text-dark-text-secondary transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1 sm:mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-[10px] sm:text-xs font-medium text-light-text-muted dark:text-dark-text-muted py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {calendarDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const status = recordsByDate[dateKey];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const today = isToday(day);

          return (
            <div
              key={index}
              className={`
                aspect-square flex flex-col items-center justify-center rounded-lg sm:rounded-xl
                transition-colors
                ${isCurrentMonth
                  ? 'text-light-text dark:text-dark-text'
                  : 'text-light-text-muted/40 dark:text-dark-text-muted/30'
                }
                ${today ? 'ring-1 ring-purple-400 dark:ring-purple-500 bg-purple-50/50 dark:bg-purple-500/10' : ''}
              `}
            >
              <span className="text-[10px] sm:text-sm font-medium">
                {format(day, 'd')}
              </span>
              <div className="mt-0.5 sm:mt-1">
                {status ? (
                  <div
                    className={`w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full ${statusColors[status]}`}
                    title={statusLabels[status]}
                  />
                ) : (
                  <div className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-light-elevated dark:bg-dark-elevated" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-light-border dark:border-dark-border flex flex-wrap gap-2 sm:gap-4 justify-center">
        {Object.keys(statusColors).map((status) => (
          <div key={status} className="flex items-center gap-1 sm:gap-1.5">
            <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${statusColors[status]}`} />
            <span className="text-[10px] sm:text-xs text-light-text-secondary dark:text-dark-text-secondary">
              {statusLabels[status]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
